/**
 * ADA / WCAG 2.1 Fixes
 * staheekum.com
 *
 * All fixes are consolidated into one file and one IIFE.
 * Deploy: save as asset "ada-newsletter-fix.js" (already referenced in
 * layout/theme.liquid just before </body>):
 *   <script src="{{ 'ada-newsletter-fix.js' | asset_url }}" defer></script>
 *
 * Fixes included:
 *   1.  Newsletter submit button — missing aria-label                (WCAG 4.1.2, Critical)
 *   2.  role="heading" missing aria-level                            (WCAG 4.1.2, Critical)
 *   3.  Photo zoom buttons — missing aria-label                      (WCAG 4.1.2, Critical)
 *   4.  Skip link — moved inside <header> landmark                   (WCAG 1.3.6, Moderate)
 *   5.  Mobile nav — <ul role="navigation"> wrapped in proper <nav>  (WCAG 4.1.2, Moderate)
 *   6.  Review date <span> — remove prohibited aria-label            (WCAG 4.1.2, Needs Review)
 *   7.  Duplicate logo images — second logo marked decorative        (WCAG 1.1.1, Low)
 *   8.  Sale price — ensure space between visually-hidden label      (WCAG 1.3.1, Low)
 *   9.  How We Care accordion buttons — missing accessible name      (WCAG 4.1.2, Critical)
 *   10. Materials & Care — broken aria-labelledby/describedby refs   (WCAG 4.1.2, Serious)
 *   11. Size chart table captions — insufficient color contrast      (WCAG 1.4.3, Serious)
 *   12. External icon-only links (trees.org, lnt.org) — aria-label  (WCAG 4.1.2, Moderate)
 *   13. Vyper iframe — missing title attribute                       (WCAG 4.1.2, Serious)
 *   14. Product thumbnail prev/next arrows — missing aria-label      (WCAG 4.1.2, Critical)
 *   15. Yotpo reviews widget — ARIA structure fixes                  (WCAG 4.1.2, Critical)
 *   16. Cart & Filter drawers — dialog semantics + focus management (WCAG 4.1.2 / 2.4.3)
 *   17. Klaviyo signup teaser — nested interactive elements          (WCAG 4.1.2, Serious)
 *   18. Klaviyo signup popup — duplicate id on email field           (WCAG 4.1.1/1.3.1, Serious)
 *   19. Klaviyo signup popup — duplicate <h1> on page                (WCAG 1.3.1, Moderate)
 *
 * Last updated: October 13, 2026
 *   - Observer callbacks are now debounced (requestAnimationFrame) so the fixes
 *     don't re-run on every single DOM mutation.
 *   - Drawer fix (16) now restores `inert` only on the elements it set it on.
 */

(function () {
  'use strict';

  // ── 1. Newsletter submit button ─────────────────────────────────────────────
  var NEWSLETTER_SELECTORS = [
    'button[id^="newsletter-submit"]',
    'button.footer__newsletter-btn',
    'button.newsletter-form__button',
    '.newsletter-form button[type="submit"]',
    'form[id^="ContactFooter"] button[type="submit"]'
  ].join(', ');

  function fixNewsletterButtons() {
    document.querySelectorAll(NEWSLETTER_SELECTORS).forEach(function (btn) {
      var existing = (btn.getAttribute('aria-label') || '').trim();
      if (!existing) {
        btn.setAttribute('aria-label', 'Subscribe to newsletter');
      }
    });
  }

  // ── 2. role="heading" missing aria-level ────────────────────────────────────
  function fixHeadingLevels() {
    document.querySelectorAll('[role="heading"]').forEach(function (el) {
      if (!el.hasAttribute('aria-level')) {
        el.setAttribute('aria-level', '2');
      }
    });
  }

  // ── 3. Photo zoom buttons ───────────────────────────────────────────────────
  function fixZoomButtons() {
    document.querySelectorAll('.js-photoswipe__zoom, .product__photo-zoom').forEach(function (btn) {
      if (!btn.getAttribute('aria-label')) {
        btn.setAttribute('aria-label', 'Zoom product image');
      }
    });
  }

  // ── 4. Skip link — move inside <header> landmark ────────────────────────────
  function fixSkipLink() {
    var skipLink = document.querySelector('a.skip-link, a.in-page-link.skip-link');
    var header = document.querySelector('header.site-header, header');
    if (skipLink && header && !header.contains(skipLink)) {
      header.insertBefore(skipLink, header.firstChild);
    }
  }

  // ── 5. Mobile nav — wrap <ul role="navigation"> in a proper <nav> ───────────
  function fixMobileNav() {
    var ul = document.querySelector('ul.mobile-nav[role="navigation"]');
    if (ul) {
      ul.removeAttribute('role');
      if (ul.parentElement.tagName.toLowerCase() !== 'nav') {
        var nav = document.createElement('nav');
        nav.setAttribute('aria-label', 'Mobile Navigation');
        ul.parentNode.insertBefore(nav, ul);
        nav.appendChild(ul);
      }
    }
  }

  // ── 6. Remove prohibited aria-label from <span> elements ────────────────────
  function fixSpanAriaLabels() {
    document.querySelectorAll('span[aria-label]').forEach(function (span) {
      // Only remove from spans with no explicit ARIA role (where aria-label is prohibited)
      if (!span.getAttribute('role')) {
        span.removeAttribute('aria-label');
      }
    });
  }

  // ── 7. Duplicate logo — mark second instance as decorative ──────────────────
  function fixDuplicateLogo() {
    var logos = document.querySelectorAll('header img[alt="Staheekum"]');
    if (logos.length > 1) {
      for (var i = 1; i < logos.length; i++) {
        logos[i].setAttribute('alt', '');
        logos[i].setAttribute('aria-hidden', 'true');
      }
    }
  }

  // ── 8. Sale price — ensure space between label and amount ───────────────────
  function fixSalePrice() {
    document.querySelectorAll('.visually-hidden').forEach(function (span) {
      var next = span.nextSibling;
      // If the next node is a bare text node with no leading space, add one
      if (next && next.nodeType === 3 && next.textContent.charAt(0) !== ' ') {
        next.textContent = ' ' + next.textContent;
      }
    });
  }

  // ── 9. How We Care — accordion buttons missing accessible name ───────────────
  //
  // The How We Care page uses GemPages builder accordions. Each toggle is a
  // <button class="wrapp-icon"> with only an icon <span> inside — no text or
  // aria-label. The visible section label lives in a sibling div.element-wrap
  // inside the same parent container. We read that text and apply it as the
  // button's aria-label. Also handles Symmetry theme accordions on other pages.
  function fixAccordionButtons() {
    // ── GemPages builder buttons (How We Care page) ──
    document.querySelectorAll('button.wrapp-icon').forEach(function (btn) {
      if ((btn.getAttribute('aria-label') || '').trim() || btn.textContent.trim()) return;
      var labelEl = btn.parentElement && btn.parentElement.querySelector('.element-wrap');
      var text = labelEl ? labelEl.textContent.trim() : '';
      if (text) {
        btn.setAttribute('aria-label', 'Toggle: ' + text);
      } else {
        var fallback = document.createElement('span');
        fallback.className = 'visually-hidden';
        fallback.textContent = 'Toggle section';
        btn.appendChild(fallback);
      }
    });

    // ── Symmetry theme / generic accordions (other pages) ──
    var symmetrySelectors = [
      '.accordion__title button',
      '.accordion button.accordion__toggle',
      '[data-accordion-toggle]',
      '.collapsible__button',
      'button[aria-controls]'
    ].join(', ');

    document.querySelectorAll(symmetrySelectors).forEach(function (btn) {
      if ((btn.getAttribute('aria-label') || '').trim() || btn.textContent.trim()) return;
      var label = '';
      var controlsId = btn.getAttribute('aria-controls');
      if (controlsId) {
        var panel = document.getElementById(controlsId);
        if (panel) {
          var panelHeading = panel.querySelector('h1,h2,h3,h4,h5,h6');
          if (panelHeading) label = panelHeading.textContent.trim();
        }
      }
      if (!label) {
        var container = btn.closest('.accordion__item, .collapsible, [data-accordion-item]');
        if (container) {
          var containerHeading = container.querySelector('h1,h2,h3,h4,h5,h6');
          if (containerHeading) label = containerHeading.textContent.trim();
        }
      }
      if (!label) {
        var prev = btn.previousElementSibling;
        if (prev && /^H[1-6]$/.test(prev.tagName)) label = prev.textContent.trim();
      }
      if (label) {
        btn.setAttribute('aria-label', label);
      } else {
        var fallback = document.createElement('span');
        fallback.className = 'visually-hidden';
        fallback.textContent = 'Toggle section';
        btn.appendChild(fallback);
      }
    });
  }

  // ── 10. Materials & Care — broken aria ref attributes ───────────────────────
  //
  // The Materials & Care page has a Polaris-generated <span class="_2QpoJ">
  // pasted into the RTE with aria-controls and aria-expanded (only valid on
  // interactive roles) pointing to a non-existent Polaris popover ID.
  // This fix has two parts:
  //   a) Strip aria-controls / aria-expanded / aria-haspopup from elements
  //      that have no interactive role or native interactivity.
  //   b) Clean up any aria-labelledby / aria-describedby / aria-owns
  //      attributes whose referenced IDs don't exist in the DOM.
  function fixBrokenAriaRefs() {
    // a) Remove interactive-only ARIA attrs from non-interactive elements
    var interactiveTags = ['button', 'a', 'input', 'select', 'textarea', 'details', 'summary'];
    var interactiveRoles = ['button', 'link', 'checkbox', 'radio', 'tab', 'menuitem',
      'combobox', 'listbox', 'option', 'switch', 'treeitem', 'gridcell'];
    ['aria-controls', 'aria-expanded', 'aria-haspopup'].forEach(function (attr) {
      document.querySelectorAll('[' + attr + ']').forEach(function (el) {
        var tag = el.tagName.toLowerCase();
        var role = (el.getAttribute('role') || '').toLowerCase();
        if (interactiveTags.indexOf(tag) === -1 && interactiveRoles.indexOf(role) === -1) {
          el.removeAttribute(attr);
        }
      });
    });

    // b) Remove broken ID references in pointer attributes
    ['aria-labelledby', 'aria-describedby', 'aria-owns'].forEach(function (attr) {
      document.querySelectorAll('[' + attr + ']').forEach(function (el) {
        var ids = el.getAttribute(attr).split(/\s+/);
        var valid = ids.filter(function (id) { return id && document.getElementById(id); });
        if (valid.length === 0) {
          el.removeAttribute(attr);
        } else if (valid.length !== ids.length) {
          el.setAttribute(attr, valid.join(' '));
        }
      });
    });
  }

  // ── 11. Size chart table captions — color contrast ──────────────────────────
  //
  // <caption> elements on size chart pages fail contrast. The table background
  // is dark (#1c1d1d), so the fix must use WHITE text — not dark text.
  // We compute the effective background luminance and pick white or dark text
  // to guarantee at least 4.5:1 contrast in either direction.
  function fixTableCaptionContrast() {
    function parseLuminance(rgbStr) {
      var m = rgbStr.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
      if (!m) return null;
      var r = parseInt(m[1], 10) / 255;
      var g = parseInt(m[2], 10) / 255;
      var b = parseInt(m[3], 10) / 255;
      // sRGB relative luminance
      r = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
      g = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
      b = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);
      return 0.2126 * r + 0.7152 * g + 0.0722 * b;
    }

    document.querySelectorAll('table caption').forEach(function (caption) {
      // Walk up to find the effective background color
      var bgLuminance = 1; // default: assume white
      var el = caption;
      while (el) {
        var bg = window.getComputedStyle(el).backgroundColor;
        if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') {
          var lum = parseLuminance(bg);
          if (lum !== null) { bgLuminance = lum; break; }
        }
        el = el.parentElement;
      }
      // Dark background → use white text; light background → use dark text
      var textColor = bgLuminance < 0.5 ? '#ffffff' : '#1a1a1a';
      caption.style.setProperty('color', textColor, 'important');
    });
  }

  // ── 12. External icon-only links — aria-label ───────────────────────────────
  //
  // Links to trees.org and lnt.org on the Plant a Tree / Sustainability pages
  // contain only an image with no descriptive text. Screen readers announce
  // the raw URL. We derive a label from the link's hostname.
  function fixExternalIconLinks() {
    document.querySelectorAll('a[href*="trees.org"], a[href*="lnt.org"]').forEach(function (a) {
      if (!a.getAttribute('aria-label') && !a.textContent.trim()) {
        try {
          var domain = new URL(a.href).hostname.replace('www.', '');
          a.setAttribute('aria-label', 'Visit ' + domain + ' (opens in new tab)');
        } catch (e) {
          a.setAttribute('aria-label', 'Visit external site (opens in new tab)');
        }
      }
    });
  }

  // ── 13. Vyper iframe — missing title attribute ───────────────────────────────
  //
  // The Vyper embed iframe has no title, so screen readers cannot describe
  // its purpose. We apply a descriptive title so users know what it contains.
  function fixVyperIframe() {
    var vyperFrame = document.getElementById('vfyps_iframe_html');
    if (vyperFrame && !vyperFrame.getAttribute('title')) {
      vyperFrame.setAttribute('title', 'Exclusive Discounts Signup');
    }
  }

  // ── 14. Product thumbnail prev/next arrows — missing aria-label ─────────────
  //
  // Product pages with more than one image render a thumbnail carousel with
  // <button class="product__thumb-arrow product__thumb-arrow--next"> and
  // ...--prev, each containing only a decorative (aria-hidden) SVG icon and
  // no text — so screen readers announce nothing when focused. Found on 22
  // of 37 product pages (any product with 2+ images). The --prev button is
  // often given class "hide" until the user scrolls, so it may not appear
  // in a single-viewport axe scan, but it has the same defect and is fixed
  // here regardless of visibility.
  function fixThumbArrowButtons() {
    document.querySelectorAll('.product__thumb-arrow--next').forEach(function (btn) {
      if (!(btn.getAttribute('aria-label') || '').trim()) {
        btn.setAttribute('aria-label', 'Next image');
      }
    });
    document.querySelectorAll('.product__thumb-arrow--prev, .product__thumb-arrow--previous').forEach(function (btn) {
      if (!(btn.getAttribute('aria-label') || '').trim()) {
        btn.setAttribute('aria-label', 'Previous image');
      }
    });
  }

  // ── 15. Yotpo reviews widget — ARIA structure fixes ──────────────────────────
  //
  // The Yotpo reviews widget (Customer Reviews page) injects markup with four
  // separate ARIA problems, verified live against the rendered widget:
  //
  //   a) Every reviewer name/title element gets aria-level="3" even though
  //      it has no role that supports aria-level (e.g. a plain <span> or
  //      <div>), which is a WCAG 4.1.2 violation (aria-allowed-attr).
  //      Fix: strip aria-level from any element whose role (or lack of one)
  //      doesn't support it.
  //
  //   b) The review-source tab list is a <div role="tablist"> whose only
  //      direct child is a plain, role-less <ul> wrapping the actual
  //      <li role="tab"> elements. That intermediate <ul> breaks the
  //      required parent/child ARIA relationship in both directions
  //      (aria-required-children on the tablist, aria-required-parent on
  //      the tab) and also trips the "list" rule (a <ul> whose children
  //      have roles other than listitem). Fix: give that wrapping <ul>
  //      role="presentation" so the accessibility tree treats the <li
  //      role="tab"> elements as direct children of the tablist, exactly
  //      as the ARIA Authoring Practices recommend for this pattern.
  //
  //   c) The pagination widget (.yotpo-pager) has role="menu", which
  //      requires every direct child to be role="menuitem" (or
  //      menuitemradio/menuitemcheckbox/group). The page-number links
  //      already use role="menuitem", but the prev/next arrow links use
  //      role="button", which breaks the parent's required-children rule.
  //      Fix: re-role the prev/next arrows to "menuitem" to match their
  //      siblings — this does not affect Yotpo's click handlers, which are
  //      bound by class/data-attribute, not by ARIA role (verified by
  //      clicking a page link after the role change: pagination still
  //      updates the active page correctly).
  //
  // Verified: after applying all three sub-fixes, axe-core reports 0
  // violations on the Customer Reviews page (previously 4: 3 critical,
  // 1 serious).
  function fixYotpoWidget() {
    // a) aria-level not allowed on elements without a supporting role
    var ARIA_LEVEL_ROLES = ['heading', 'row', 'treeitem', 'listitem', 'comment'];
    document.querySelectorAll('.yotpo-widget-instance [aria-level], .yotpo [aria-level]').forEach(function (el) {
      var role = (el.getAttribute('role') || '').toLowerCase();
      var tag = el.tagName.toLowerCase();
      var nativeHeading = /^h[1-6]$/.test(tag);
      if (!nativeHeading && ARIA_LEVEL_ROLES.indexOf(role) === -1) {
        el.removeAttribute('aria-level');
      }
    });

    // b) Yotpo tab list: <ul> wrapping li[role=tab] under a tablist needs role=presentation
    document.querySelectorAll('.yotpo-nav[role="tablist"], .yotpo-popup-box-small[role="tablist"]').forEach(function (tablist) {
      Array.prototype.forEach.call(tablist.children, function (child) {
        if (child.tagName.toLowerCase() === 'ul' && !child.getAttribute('role')) {
          child.setAttribute('role', 'presentation');
        }
      });
    });

    // c) Yotpo pager: re-role prev/next arrows to menuitem to match sibling page links
    document.querySelectorAll('.yotpo-pager[role="menu"] > [role="button"]').forEach(function (el) {
      el.setAttribute('role', 'menuitem');
    });
  }

  // ── Run all fixes ────────────────────────────────────────────────────────────
  function runAllFixes() {
    fixNewsletterButtons();
    fixHeadingLevels();
    fixZoomButtons();
    fixSkipLink();
    fixMobileNav();
    fixSpanAriaLabels();
    fixDuplicateLogo();
    fixSalePrice();
    fixAccordionButtons();
    fixBrokenAriaRefs();
    fixTableCaptionContrast();
    fixExternalIconLinks();
    fixVyperIframe();
    fixThumbArrowButtons();
    fixYotpoWidget();
  }

  // Run immediately if DOM is ready, otherwise wait for DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runAllFixes);
  } else {
    runAllFixes();
  }

  // Also run after a short delay to catch theme JS that injects elements late
  setTimeout(runAllFixes, 800);

  // Yotpo loads asynchronously and can take longer than other widgets; give it
  // an extra pass.
  setTimeout(runAllFixes, 2000);

  // Watch for any dynamically injected elements (overlays, carousels, sections).
  // Debounced: bursts of DOM changes trigger ONE pass on the next animation
  // frame instead of one full pass per mutation batch.
  if (typeof MutationObserver !== 'undefined') {
    var fixScheduled = false;
    var observer = new MutationObserver(function (mutations) {
      if (fixScheduled) return;
      if (mutations.some(function (m) {
        return m.type === 'childList' && m.addedNodes.length > 0;
      })) {
        fixScheduled = true;
        window.requestAnimationFrame(function () {
          fixScheduled = false;
          runAllFixes();
        });
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

})();


(function () {
  var observer = new MutationObserver(function () {
    var banner = document.getElementById('shopify-pc__banner');
    if (banner && !banner.hasAttribute('aria-label')) {
      banner.setAttribute('aria-label', 'Cookie consent');
      observer.disconnect();
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();


function stripReviewTabindex() {
  document.querySelectorAll('.standard-layout.single-carousel.review[tabindex="0"]')
    .forEach(function (el) { el.setAttribute('tabindex', '-1'); });
}
document.addEventListener('DOMContentLoaded', stripReviewTabindex);
new MutationObserver(stripReviewTabindex).observe(document.body, { childList: true, subtree: true });


// ── 16. Cart & Filter drawers — dialog semantics + focus management ────────
//
// #CartDrawer and #FilterDrawer are plain <div>s with no role="dialog" or
// aria-modal. The theme opens/closes them itself (toggling the class
// "drawer--is-open" — verified live), so rather than replacing that logic,
// this watches for the class change and layers in the missing dialog
// semantics, an inert background, and focus trap/return.
//
// Important: #FilterDrawer is nested INSIDE <main id="MainContent"> (it
// lives inside the collection sidebar), while #CartDrawer lives outside it.
// An earlier version of this fix hardcoded #MainContent as "the background
// to inert," which worked for the cart drawer but made the filter drawer
// inert *itself* (inert cascades to all descendants), silently breaking
// focus on its own close button. setBackgroundInert() below fixes this by
// inerting siblings at every level up to <body>, so whichever drawer opens,
// only its own branch of the tree stays interactive — regardless of where
// that drawer happens to sit in the DOM.
//
// Oct 2026: the drawer now remembers exactly which elements IT made inert and
// restores only those on close, so it never clears `inert` that something else
// (e.g. another modal) had set.
(function () {
  var lastFocusedEl = null;
  var inertedByDrawer = [];

  function isOpen(drawer) {
    return drawer.classList.contains('drawer--is-open');
  }

  function setBackgroundInert(el) {
    inertedByDrawer = [];
    var node = el;
    while (node && node !== document.body) {
      var parent = node.parentElement;
      if (parent) {
        Array.prototype.forEach.call(parent.children, function (sibling) {
          if (sibling !== node && !sibling.hasAttribute('inert')) {
            sibling.setAttribute('inert', '');
            inertedByDrawer.push(sibling);
          }
        });
      }
      node = parent;
    }
  }

  function restoreBackground() {
    inertedByDrawer.forEach(function (el) { el.removeAttribute('inert'); });
    inertedByDrawer = [];
  }

  function activateDrawer(drawer) {
    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', 'true');
    drawer.setAttribute('aria-hidden', 'false');
    lastFocusedEl = document.activeElement;
    setBackgroundInert(drawer);
    var closeBtn = drawer.querySelector('.drawer__close-button');
    (closeBtn || drawer).focus();
  }

  function deactivateDrawer(drawer) {
    drawer.setAttribute('aria-hidden', 'true');
    restoreBackground();
    if (lastFocusedEl) lastFocusedEl.focus();
  }

  function watchDrawer(selector) {
    var drawer = document.querySelector(selector);
    if (!drawer) return;

    var titleEl = drawer.querySelector('.drawer__title, .h2, h2, h3');
    if (titleEl) {
      if (!titleEl.id) titleEl.id = drawer.id + '-title';
      drawer.setAttribute('aria-labelledby', titleEl.id);
    }
    drawer.setAttribute('aria-hidden', 'true');

    var wasOpen = isOpen(drawer);
    new MutationObserver(function () {
      var nowOpen = isOpen(drawer);
      if (nowOpen && !wasOpen) activateDrawer(drawer);
      if (!nowOpen && wasOpen) deactivateDrawer(drawer);
      wasOpen = nowOpen;
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  watchDrawer('#CartDrawer');
  watchDrawer('#FilterDrawer');
})();


// ── 17. Klaviyo signup teaser — nested interactive elements ─────────────────
//
// The collapsed "teaser" bubble that precedes the Klaviyo signup popup
// (class containing "kl-teaser") renders a "Close teaser" <button> nested
// INSIDE the "Open Form" <button>. A button inside a button is invalid
// markup and breaks keyboard focus/activation semantics for screen reader
// and keyboard-only users — confirmed still present in Klaviyo's own
// hosted form config (form id VFEbRi, flagged by Klaviyo's own tooling as
// axe rule "nested-interactive", impact: serious, first reported May 2023,
// still served as of this writing).
//
// This is a separate component from the popup dialog itself, so it's not
// covered by the existing Klaviyo dialog patch earlier in this file, which
// only looks for [role="dialog"]. This fix moves the close button out to
// be a sibling instead of a descendant — Klaviyo's own inline absolute
// positioning keeps it visually in the same spot.
function fixKlaviyoTeaser() {
  var teaserBtn = document.querySelector('[class*="kl-teaser"]');
  if (!teaserBtn || teaserBtn.dataset.a11yTeaserFixed) return;
  var closeBtn = teaserBtn.querySelector('button, a, [role="button"]');
  if (closeBtn && closeBtn !== teaserBtn && teaserBtn.contains(closeBtn)) {
    teaserBtn.parentNode.insertBefore(closeBtn, teaserBtn.nextSibling);
  }
  teaserBtn.dataset.a11yTeaserFixed = 'true';
}
document.addEventListener('DOMContentLoaded', fixKlaviyoTeaser);
setTimeout(fixKlaviyoTeaser, 800);
setTimeout(fixKlaviyoTeaser, 2000);
new MutationObserver(function (mutations) {
  mutations.forEach(function (m) {
    m.addedNodes.forEach(function (n) { if (n.nodeType === 1) fixKlaviyoTeaser(); });
  });
}).observe(document.body, { childList: true, subtree: true });


// ── 18. Klaviyo signup popup — duplicate id on email field ──────────────────
//
// The popup's email <input> ships from Klaviyo with a hardcoded id
// (e.g. "email_70999265" in the current config) that collides with
// another id already present on the page — confirmed still present in
// Klaviyo's own hosted form config (flagged by Klaviyo's own tooling as
// axe rule "duplicate-id-active", impact: serious, first reported May
// 2023, still served as of this writing). Duplicate ids break label
// association for assistive technology.
//
// This gives any colliding Klaviyo email input a guaranteed-unique id and
// repoints any <label for="..."> or aria-labelledby that referenced the
// old id, before changing it — so there's never a moment with a dangling
// reference.
function fixKlaviyoDuplicateEmailId() {
  document.querySelectorAll('input[type="email"][id]').forEach(function (input) {
    var id = input.id;
    if (input.dataset.a11yIdFixed) return;
    if (document.querySelectorAll('#' + CSS.escape(id)).length <= 1) return;
    var newId = 'kl-popup-email-' + Date.now() + '-' + Math.floor(Math.random() * 1000);
    document.querySelectorAll('label[for="' + CSS.escape(id) + '"]').forEach(function (lbl) {
      lbl.setAttribute('for', newId);
    });
    document.querySelectorAll('[aria-labelledby~="' + CSS.escape(id) + '"]').forEach(function (el) {
      el.setAttribute('aria-labelledby', el.getAttribute('aria-labelledby').split(/\s+/).map(function (t) {
        return t === id ? newId : t;
      }).join(' '));
    });
    input.id = newId;
    input.dataset.a11yIdFixed = 'true';
  });
}
document.addEventListener('DOMContentLoaded', fixKlaviyoDuplicateEmailId);
setTimeout(fixKlaviyoDuplicateEmailId, 800);
setTimeout(fixKlaviyoDuplicateEmailId, 2000);
new MutationObserver(function (mutations) {
  mutations.forEach(function (m) {
    m.addedNodes.forEach(function (n) { if (n.nodeType === 1) fixKlaviyoDuplicateEmailId(); });
  });
}).observe(document.body, { childList: true, subtree: true });

// ── 19. Klaviyo signup popup — duplicate <h1> on page ────────────────────────
//
// The popup dialog's title ("Join Staheekum for 10% off your first order.")
// renders as a native <h1>. Every page already has its own topic <h1> (the
// product title, collection name, etc.), so when the popup opens the page
// briefly has two h1s. Screen reader users navigating by heading level
// expect exactly one h1 = page topic; a second, unrelated h1 breaks that
// expectation and clutters heading-list navigation.
//
// This doesn't touch the popup's actual DOM tag (still <h1> — leaving the
// tag alone avoids any risk of breaking Klaviyo's own styling/selectors,
// which may key off the tag itself). Instead it overrides the *accessible*
// heading level with an explicit role + aria-level, the same technique
// fix #2 in this file already uses for role="heading" elements elsewhere.
// Explicit role/aria-level takes precedence over a native element's
// implicit semantics for every screen reader.
//
// Reuses the existing Klaviyo dialog patch's own h1 lookup pattern
// (dialog.querySelector('h1, [role="heading"]')) so this only ever touches
// the popup's title, not any other h1 on the page.
function fixKlaviyoPopupHeadingLevel() {
  document.querySelectorAll('[role="dialog"].kl-private-reset-css-Xuajs1').forEach(function (dialog) {
    var h1 = dialog.querySelector('h1');
    if (!h1 || h1.dataset.a11yLevelFixed) return;
    h1.setAttribute('role', 'heading');
    h1.setAttribute('aria-level', '2');
    h1.dataset.a11yLevelFixed = 'true';
  });
}
document.addEventListener('DOMContentLoaded', fixKlaviyoPopupHeadingLevel);
setTimeout(fixKlaviyoPopupHeadingLevel, 800);
setTimeout(fixKlaviyoPopupHeadingLevel, 2000);
new MutationObserver(function (mutations) {
  mutations.forEach(function (m) {
    m.addedNodes.forEach(function (n) { if (n.nodeType === 1) fixKlaviyoPopupHeadingLevel(); });
  });
}).observe(document.body, { childList: true, subtree: true });